import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '@familywallet/api-client';
import {
  type Account,
  type ForeignExpenseRequest,
  parseRateInput,
  planForeignExpense,
  sortCurrencies,
  splitPaymentSummary,
  type Transaction,
  type TransactionDefaults,
} from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type FieldError, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { LimitWarning } from '../components/PlanningCards';
import { FormError } from '../components/FormError';
import { Chip, Row } from '../components/FormRow';
import { MoneyField } from '../components/MoneyField';
import { PickerSheet } from '../components/PickerSheet';
import { Button } from '../components/ui/button';
import { Card, ListGroup } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { parsePositive } from '../lib/add-form';
import { api } from '../lib/api';
import { todayIn } from '../lib/dates';
import { useCan } from '../lib/auth';
import {
  expenseCurrencyOf,
  type ForeignExpenseFormValues,
  foreignExpenseFormSchema,
  foreignSplitRemaining,
  fromSplitTransaction,
  parseBill,
  parsedPaymentLines,
} from '../lib/fx-forms';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { useIdempotencyKey } from '../lib/members';
import { formatAmount, formatRateValue, groupAmountInput } from '../lib/money';
import { cn } from '../lib/utils';
import { CategoryChip, CategoryPicker, PayeeRow } from './AddPage';
import { PersonPicker } from './NewDebtPage';

const currenciesOf = (account: Account | undefined, baseCurrency: string) =>
  sortCurrencies(account?.balances.map((b) => b.currency) ?? [], baseCurrency);

/**
 * An expense (or income) paid with several payments, each with its own amount, currency and
 * account, with change returned in any currency, as one transaction. Shows what counts as spent
 * as the person types: the bill with the shop's rate next to the family's, or, with no bill,
 * paid minus returned at the family rate, marked as an estimate. Under the payments: the total
 * paid in the family currency and what remains (or was paid in excess). Part on credit makes
 * what remains a debt.
 */
export function ForeignExpenseForm({
  kind = 'expense',
  accounts,
  defaults,
  baseCurrency,
  timezone,
  latestRates,
  myId,
  familyCurrencies,
  correcting,
}: {
  kind?: 'expense' | 'income';
  accounts: Account[];
  defaults: TransactionDefaults;
  baseCurrency: string;
  timezone: string;
  latestRates: Record<string, string>;
  myId: string;
  /** Currencies the family uses: the bill and the change may be in any of them. */
  familyCurrencies: readonly string[];
  /** Correct: the transaction being corrected fills the form; saving replaces it. */
  correcting?: Transaction;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateLedger();
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const today = todayIn(timezone);
  const byId = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const [picker, setPicker] = useState<
    | { type: 'account'; list: 'payments' | 'returns'; index: number }
    | { type: 'category'; line: number | null }
    | null
  >(null);
  const [payeeFocused, setPayeeFocused] = useState(false);
  const [rateWanted, setRateWanted] = useState(false);
  // The excess the person was asked about; a changed amount asks again.
  const [askedExcess, setAskedExcess] = useState<string | null>(null);
  const original = correcting ? fromSplitTransaction(correcting) : null;
  const [personPicker, setPersonPicker] = useState(false);
  const [personLabel, setPersonLabel] = useState(original?.personLabel ?? '');
  const canOutside = useCan('debts.outside');
  const canManagePeople = useCan('people.manage');
  const canAddPeople = canOutside || canManagePeople;
  // The form reads through a ref whether the rate field is shown: it changes the checks.
  const askRateRef = useRef(false);

  // The account used last for this kind, else my first one.
  const start =
    byId.get(defaults.lastAccountId[kind] ?? '') ??
    accounts.find((a) => a.ownerMemberId === myId) ??
    accounts[0]!;
  const startCurrencies = currenciesOf(start, baseCurrency);
  const all = sortCurrencies([...familyCurrencies], baseCurrency);
  const paidStart = startCurrencies[0] ?? baseCurrency;
  const other = all.find((c) => c !== paidStart) ?? paidStart;

  const form = useForm<ForeignExpenseFormValues, unknown, ForeignExpenseRequest>({
    resolver: (values, context, options) =>
      zodResolver(foreignExpenseFormSchema(askRateRef.current, baseCurrency))(
        values,
        context,
        options,
      ),
    defaultValues: {
      kind,
      categoryId: '',
      billAmount: '',
      // Bills are usually priced in the family currency.
      billCurrency: baseCurrency,
      payments: [{ accountId: start.id, amount: '', currency: paidStart }],
      returns: [{ accountId: start.id, amount: '', currency: other }],
      payeeName: '',
      occurredOn: today,
      description: '',
      sharedWithFamily: false,
      rate: '',
      split: false,
      lines: [],
      credit: false,
      personId: '',
      personName: '',
      dueOn: '',
      ...original?.values,
    },
  });
  const v = useWatch({ control: form.control }) as ForeignExpenseFormValues;
  const splitLines = useFieldArray({ control: form.control, name: 'lines' });
  const payments = useFieldArray({ control: form.control, name: 'payments' });
  const returns = useFieldArray({ control: form.control, name: 'returns' });

  // Each payment: a currency its account holds. The change may be in any currency the family
  // uses; one the account does not hold yet is added to it when saved.
  useEffect(() => {
    v.payments.forEach((p, i) => {
      const held = currenciesOf(byId.get(p.accountId), baseCurrency);
      if (!held.includes(p.currency) && held[0]) {
        form.setValue(`payments.${i}.currency`, held[0]);
      }
    });
  }, [v.payments, byId, baseCurrency, form]);

  // Live result, with today's family rate (the API uses the rate of the chosen day).
  const parsed = parsedPaymentLines(v);
  const bill = parseBill(v.billAmount, v.billCurrency);
  const oneOff = parseRateInput(v.rate);
  const rates = { ...latestRates };
  const currencies = new Set([
    ...v.payments.map((p) => p.currency),
    ...v.returns.map((r) => r.currency),
  ]);
  const foreign = [...currencies].find((c) => c !== baseCurrency);
  if (rateWanted && oneOff && foreign) rates[foreign] = oneOff;
  const plan =
    parsed && bill !== undefined
      ? planForeignExpense(
          {
            paid: parsed.paid,
            returned: parsed.returned,
            ...(bill ? { bill: { amount: bill, currency: v.billCurrency } } : {}),
            onCredit: v.credit && bill !== null,
          },
          baseCurrency,
          rates,
        )
      : null;
  const summary =
    parsed && bill !== undefined
      ? splitPaymentSummary(
          {
            paid: parsed.paid,
            returned: parsed.returned,
            ...(bill ? { bill: { amount: bill, currency: v.billCurrency } } : {}),
          },
          baseCurrency,
          rates,
        )
      : null;
  const needsRate =
    !bill && plan !== null && !plan.ok && plan.code === 'rate.required' && !rateWanted;
  const askRate = rateWanted && !bill;
  useEffect(() => {
    askRateRef.current = askRate;
  }, [askRate]);
  // Paid more than the bill with no change returned: ask before saving.
  const excess = summary && bill && summary.excess !== '0.0000' ? summary.excess : null;
  const needsExcessCheck = excess !== null && (parsed?.returned.length ?? 0) === 0;
  const askExcess = askedExcess !== null && askedExcess === excess;

  const save = useMutation({
    mutationFn: (body: ForeignExpenseRequest) =>
      correcting
        ? api.transactions.correctForeignExpense(correcting.id, body, { idempotencyKey })
        : api.transactions.foreignExpense(body, { idempotencyKey }),
    onSuccess: async (saved) => {
      renewKey();
      await invalidate();
      if (correcting) navigate(`/activity/${saved.id}`, { replace: true });
      else navigate('/', { state: { saved: true } });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'rate.required') setRateWanted(true);
    },
  });

  const errors = form.formState.errors;
  const errorText = (error: FieldError | undefined, currency = '') =>
    error?.message ? t(error.message, { currency }) : null;
  const reformat = (
    name: 'billAmount' | `payments.${number}.amount` | `returns.${number}.amount`,
    currency: string,
  ) => {
    const typed = parsePositive(form.getValues(name), currency);
    if (typed) form.setValue(name, groupAmountInput(typed, currency));
  };
  const familyRate =
    plan?.ok && plan.effectiveRate ? latestRates[plan.effectiveRate.currency] : null;
  const preview = (raw: string, currency: string) => ({ raw, baseCurrency, rate: rates[currency] });
  const sharable = [...v.payments, ...v.returns].some((p) => byId.get(p.accountId)?.isPrivate);

  // A split shares what was spent, in its currency.
  const expenseCurrency = plan?.ok ? plan.expense.currency : expenseCurrencyOf(v, baseCurrency);
  const remaining =
    v.split && plan?.ok
      ? foreignSplitRemaining(v.lines, plan.expense.amount, expenseCurrency)
      : null;
  const splitOpen = v.split && remaining !== null && remaining !== '0.0000';
  const startSplit = () => {
    form.setValue('split', true);
    const first = plan?.ok ? groupAmountInput(plan.expense.amount, expenseCurrency) : '';
    splitLines.replace([
      { categoryId: v.categoryId, amount: first, note: '' },
      { categoryId: '', amount: '', note: '' },
    ]);
  };
  const stopSplit = () => {
    form.setValue('split', false);
    form.setValue('categoryId', v.lines[0]?.categoryId ?? v.categoryId);
    splitLines.replace([]);
  };
  const reformatLine = (i: number) => {
    const parsed = parsePositive(form.getValues(`lines.${i}.amount`), expenseCurrency);
    if (parsed) form.setValue(`lines.${i}.amount`, groupAmountInput(parsed, expenseCurrency));
  };

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((body) => save.mutate(body))}
    >
      <p className="px-1 text-sm text-muted">{t('foreign.intro')}</p>
      <MoneyField
        id="foreign-bill"
        label={t('foreign.bill')}
        hint={t('foreign.billHint')}
        register={form.register('billAmount', {
          onBlur: () => reformat('billAmount', v.billCurrency),
        })}
        currency={v.billCurrency}
        currencies={all}
        onCurrency={(c) => form.setValue('billCurrency', c)}
        error={errorText(errors.billAmount, v.billCurrency)}
        preview={preview(v.billAmount, v.billCurrency)}
      />
      <section className="flex flex-col gap-2" aria-label={t('foreign.payments')}>
        {payments.fields.map((field, i) => {
          const line = v.payments[i];
          if (!line) return null;
          const lineAccount = byId.get(line.accountId);
          return (
            <MoneyField
              key={field.id}
              id={`foreign-paid-${i}`}
              label={t(`foreign.paid_${kind}`)}
              register={form.register(`payments.${i}.amount`, {
                onBlur: () => reformat(`payments.${i}.amount`, line.currency),
              })}
              currency={line.currency}
              currencies={currenciesOf(lineAccount, baseCurrency)}
              onCurrency={(c) => form.setValue(`payments.${i}.currency`, c)}
              error={errorText(
                errors.payments?.[i]?.amount ?? errors.payments?.[i]?.accountId,
                line.currency,
              )}
              preview={preview(line.amount, line.currency)}
              footer={
                <LineAccount
                  name={lineAccount?.name}
                  onPick={() => setPicker({ type: 'account', list: 'payments', index: i })}
                  onRemove={payments.fields.length > 1 ? () => payments.remove(i) : undefined}
                  removeLabel={t('foreign.removePayment', { n: i + 1 })}
                />
              }
            />
          );
        })}
        <Button
          variant="ghost"
          disabled={payments.fields.length >= 10}
          onClick={() => {
            const last = v.payments.at(-1);
            const next =
              accounts.find((a) => !v.payments.some((p) => p.accountId === a.id)) ?? start;
            payments.append({
              accountId: next.id,
              amount: '',
              currency:
                currenciesOf(next, baseCurrency).find((c) => c !== last?.currency) ??
                currenciesOf(next, baseCurrency)[0] ??
                baseCurrency,
            });
          }}
        >
          <Plus aria-hidden="true" className="size-5" strokeWidth={2} />
          {t('foreign.addPayment')}
        </Button>
      </section>

      <section className="flex flex-col gap-2" aria-label={t('foreign.returns')}>
        {returns.fields.map((field, i) => {
          const line = v.returns[i];
          if (!line) return null;
          const lineAccount = byId.get(line.accountId);
          const added =
            line.amount.trim() !== '' &&
            lineAccount !== undefined &&
            !currenciesOf(lineAccount, baseCurrency).includes(line.currency);
          return (
            <MoneyField
              key={field.id}
              id={`foreign-returned-${i}`}
              label={t(`foreign.returned_${kind}`)}
              hint={t('foreign.returnedHint')}
              register={form.register(`returns.${i}.amount`, {
                onBlur: () => reformat(`returns.${i}.amount`, line.currency),
              })}
              currency={line.currency}
              currencies={all}
              onCurrency={(c) => form.setValue(`returns.${i}.currency`, c)}
              error={errorText(
                errors.returns?.[i]?.amount ?? errors.returns?.[i]?.accountId,
                line.currency,
              )}
              preview={preview(line.amount, line.currency)}
              footer={
                <>
                  <LineAccount
                    name={lineAccount?.name}
                    onPick={() => setPicker({ type: 'account', list: 'returns', index: i })}
                    onRemove={() => returns.remove(i)}
                    removeLabel={t('foreign.removeReturned', { n: i + 1 })}
                  />
                  {added && (
                    <p className="text-sm text-muted">
                      {t('foreign.addedCurrency', {
                        currency: line.currency,
                        account: lineAccount.name,
                      })}
                    </p>
                  )}
                </>
              }
            />
          );
        })}
        <Button
          variant="ghost"
          disabled={returns.fields.length >= 10}
          onClick={() =>
            returns.append({
              accountId: v.payments[0]?.accountId ?? start.id,
              amount: '',
              currency: other,
            })
          }
        >
          <Plus aria-hidden="true" className="size-5" strokeWidth={2} />
          {t('foreign.addReturned')}
        </Button>
      </section>

      {summary && (summary.paid !== '0.0000' || bill) && (
        <ListGroup aria-live="polite">
          <SummaryRow
            label={t(`foreign.totalPaid_${kind}`)}
            value={`≈ ${formatAmount(summary.paid, baseCurrency, i18n.language)}`}
          />
          {summary.bill !== null && summary.remaining !== '0.0000' && (
            <SummaryRow
              label={t('foreign.remaining')}
              value={formatAmount(summary.remaining, baseCurrency, i18n.language)}
              tone="danger"
            />
          )}
          {summary.bill !== null && summary.excess !== '0.0000' && (
            <SummaryRow
              label={t('foreign.excess')}
              value={formatAmount(summary.excess, baseCurrency, i18n.language)}
              tone="danger"
            />
          )}
          {summary.bill !== null &&
            summary.remaining === '0.0000' &&
            summary.excess === '0.0000' && <SummaryRow label={t('foreign.settled')} value="✓" />}
        </ListGroup>
      )}
      {summary && <p className="-mt-2 px-1 text-xs text-muted">{t('foreign.summaryHint')}</p>}

      {plan && (
        <Card className="flex flex-col gap-1" aria-live="polite">
          {plan.ok ? (
            <>
              <span className="text-[13px] text-muted">{t(`foreign.spent_${kind}`)}</span>
              <span dir="ltr" className="text-[26px] font-semibold tabular-nums">
                {formatAmount(plan.expense.amount, plan.expense.currency, i18n.language)}
              </span>
              {plan.estimated && (
                <span className="text-sm text-muted">{t('foreign.estimated')}</span>
              )}
              {plan.creditLine && (
                <span dir="ltr" className="text-sm text-muted">
                  {t('credit.onCredit')}:{' '}
                  {formatAmount(
                    plan.creditLine.amount.replace('-', ''),
                    plan.creditLine.currency,
                    i18n.language,
                  )}
                </span>
              )}
              {plan.effectiveRate && (
                <span dir="ltr" className="text-sm text-muted">
                  {t('foreign.shopRate', {
                    base: baseCurrency,
                    rate: formatRateValue(plan.effectiveRate.rate, i18n.language),
                    quote: plan.effectiveRate.currency,
                  })}
                  {familyRate &&
                    ` · ${t('exchange.familyRate', { rate: formatRateValue(familyRate, i18n.language) })}`}
                </span>
              )}
            </>
          ) : (
            <span className="text-sm text-danger">
              {t(`foreign.errors.${plan.code.replace('.', '_')}`, { currency: plan.currency })}
            </span>
          )}
        </Card>
      )}
      {needsRate && (
        <Button variant="secondary" onClick={() => setRateWanted(true)}>
          {t('foreign.enterRate')}
        </Button>
      )}
      {askRate && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="foreign-rate" className="text-[13px] font-medium text-muted">
            {t('rates.oneOffLabel', { base: baseCurrency, quote: foreign ?? '' })}
          </label>
          <Input
            id="foreign-rate"
            inputMode="decimal"
            autoComplete="off"
            dir="ltr"
            placeholder="89,500"
            aria-invalid={errors.rate ? true : undefined}
            {...form.register('rate')}
          />
          {errors.rate && <p className="text-sm text-danger">{errorText(errors.rate)}</p>}
        </div>
      )}

      <ListGroup>
        <PayeeRow
          kind={kind}
          value={v.payeeName}
          focused={payeeFocused}
          onFocus={() => setPayeeFocused(true)}
          onBlur={() => setTimeout(() => setPayeeFocused(false), 150)}
          register={form.register('payeeName')}
          onPick={(payee) => {
            form.setValue('payeeName', payee.name);
            setPayeeFocused(false);
            if (payee.suggestedCategoryId && !v.categoryId && !v.split) {
              form.setValue('categoryId', payee.suggestedCategoryId);
            }
          }}
        />
        {!v.split && (
          <Row label={t('add.category')} error={errorText(errors.categoryId)}>
            <CategoryChip
              kind={kind}
              id={v.categoryId}
              onClick={() => setPicker({ type: 'category', line: null })}
            />
          </Row>
        )}
        <Row label={t('add.date')} htmlFor="foreign-date" error={errorText(errors.occurredOn)}>
          <input
            id="foreign-date"
            type="date"
            max={today}
            className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
            {...form.register('occurredOn')}
          />
        </Row>
        <Row label={t('add.note')} htmlFor="foreign-note">
          <input
            id="foreign-note"
            autoComplete="off"
            maxLength={200}
            placeholder={t('add.notePlaceholder')}
            className="min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] placeholder:text-muted focus-visible:outline-none"
            {...form.register('description')}
          />
        </Row>
      </ListGroup>

      {v.split && (
        <section className="flex flex-col gap-2" aria-label={t('add.splitTitle')}>
          {plan?.ok && (
            <p className="px-1 text-sm text-muted">
              {t('foreign.splitOf', {
                amount: formatAmount(plan.expense.amount, plan.expense.currency, i18n.language),
              })}
            </p>
          )}
          <ListGroup>
            {splitLines.fields.map((field, i) => (
              <li key={field.id} className="flex flex-col gap-2 px-4 py-3">
                <div className="flex items-center gap-2">
                  <CategoryChip
                    kind={kind}
                    id={v.lines[i]?.categoryId ?? ''}
                    onClick={() => setPicker({ type: 'category', line: i })}
                  />
                  <input
                    aria-label={t('add.lineAmount', { n: i + 1 })}
                    inputMode={expenseCurrency === 'LBP' ? 'text' : 'decimal'}
                    autoComplete="off"
                    dir="ltr"
                    placeholder={expenseCurrency === 'LBP' ? '100k' : '0.00'}
                    className="min-h-11 w-full min-w-0 flex-1 rounded-2xl border border-line bg-surface px-3 text-end text-[15px] tabular-nums focus-visible:outline-2 focus-visible:outline-accent"
                    {...form.register(`lines.${i}.amount`, { onBlur: () => reformatLine(i) })}
                  />
                  {splitLines.fields.length > 1 && (
                    <button
                      type="button"
                      aria-label={t('add.removeLine', { n: i + 1 })}
                      onClick={() => splitLines.remove(i)}
                      className="flex size-11 shrink-0 items-center justify-center rounded-2xl text-muted hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
                    >
                      <Trash2 aria-hidden="true" className="size-5" strokeWidth={2} />
                    </button>
                  )}
                </div>
                <input
                  aria-label={t('add.lineNote', { n: i + 1 })}
                  autoComplete="off"
                  maxLength={200}
                  placeholder={t('add.lineNotePlaceholder')}
                  className="min-h-11 w-full bg-transparent text-[13px] placeholder:text-muted focus-visible:outline-none"
                  {...form.register(`lines.${i}.note`)}
                />
                {(errors.lines?.[i]?.categoryId || errors.lines?.[i]?.amount) && (
                  <p className="text-sm text-danger">
                    {errorText(
                      errors.lines?.[i]?.categoryId ?? errors.lines?.[i]?.amount,
                      expenseCurrency,
                    )}
                  </p>
                )}
              </li>
            ))}
          </ListGroup>
          <div className="flex items-center justify-between gap-3 px-1">
            <Button
              variant="ghost"
              onClick={() => splitLines.append({ categoryId: '', amount: '', note: '' })}
              disabled={splitLines.fields.length >= 20}
            >
              <Plus aria-hidden="true" className="size-5" strokeWidth={2} />
              {t('add.addLine')}
            </Button>
            <span
              aria-live="polite"
              className={cn('text-[13px] tabular-nums', splitOpen ? 'text-danger' : 'text-muted')}
            >
              {remaining === null
                ? null
                : remaining === '0.0000'
                  ? t('add.splitBalanced')
                  : t('add.remaining', {
                      amount: formatAmount(remaining, expenseCurrency, i18n.language),
                    })}
            </span>
          </div>
        </section>
      )}
      <Button variant="secondary" onClick={v.split ? stopSplit : startSplit}>
        {v.split ? t('add.unsplit') : t('add.split')}
      </Button>

      <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-3xl border border-line bg-surface px-4 py-3">
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="font-medium">{t('credit.toggle')}</span>
          <span id="foreign-credit-hint" className="text-sm text-muted">
            {t(`foreign.creditHint_${kind}`)}
          </span>
        </span>
        <input
          type="checkbox"
          role="switch"
          aria-label={t('credit.toggle')}
          aria-describedby="foreign-credit-hint"
          className="size-5 shrink-0 accent-accent"
          {...form.register('credit')}
        />
      </label>
      {v.credit && (
        <ListGroup aria-label={t('credit.toggle')}>
          <Row
            label={t(kind === 'expense' ? 'credit.owedTo' : 'credit.owedBy')}
            error={errorText(errors.personId)}
          >
            <Chip onClick={() => setPersonPicker(true)}>
              {personLabel || t('debts.pickPerson')}
            </Chip>
          </Row>
          <Row label={t('credit.dueOn')} htmlFor="foreign-credit-due">
            <input
              id="foreign-credit-due"
              type="date"
              className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
              {...form.register('dueOn')}
            />
          </Row>
        </ListGroup>
      )}

      {sharable && (
        <label className="flex min-h-11 items-start gap-3 px-1">
          <input
            type="checkbox"
            className="mt-1 size-5 accent-accent"
            {...form.register('sharedWithFamily')}
          />
          <span className="flex flex-col">
            <span className="font-medium">{t('add.share')}</span>
            <span className="text-sm text-muted">{t('add.shareHint')}</span>
          </span>
        </label>
      )}

      {kind === 'expense' && (
        <LimitWarning
          memberId={myId}
          amount={plan?.ok ? plan.expense.amount : null}
          currency={expenseCurrency}
          baseCurrency={baseCurrency}
          rate={latestRates[expenseCurrency]}
        />
      )}
      <FormError error={save.error} />
      {askExcess ? (
        <Card className="flex flex-col gap-3" role="alert">
          <p className="font-medium">
            {t('foreign.excessConfirm', {
              amount: formatAmount(excess ?? '0', baseCurrency, i18n.language),
            })}
          </p>
          <p className="text-sm text-muted">{t('foreign.excessConfirmHint')}</p>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t('common.saving') : t('foreign.saveAnyway')}
          </Button>
          <Button variant="ghost" onClick={() => setAskedExcess(null)}>
            {t('txDetail.cancel')}
          </Button>
        </Card>
      ) : (
        <Button
          type={needsExcessCheck ? 'button' : 'submit'}
          onClick={needsExcessCheck ? () => setAskedExcess(excess) : undefined}
          disabled={
            save.isPending ||
            splitOpen ||
            (plan !== null && !plan.ok && plan.code !== 'rate.required')
          }
        >
          {save.isPending ? t('common.saving') : t('add.save')}
        </Button>
      )}

      {personPicker && (
        <PersonPicker
          members={[]}
          canAdd={canAddPeople}
          selected={v.personId}
          onPickPerson={(id, name) => {
            form.setValue('personId', id);
            form.setValue('personName', '');
            setPersonLabel(name);
            setPersonPicker(false);
          }}
          onPickName={(name) => {
            form.setValue('personId', '');
            form.setValue('personName', name);
            setPersonLabel(name);
            setPersonPicker(false);
          }}
          onPickMember={() => setPersonPicker(false)}
          onClose={() => setPersonPicker(false)}
        />
      )}
      {picker?.type === 'account' && (
        <PickerSheet
          title={t('add.pickAccount')}
          options={accounts.map((a) => ({
            value: a.id,
            label: a.name,
            hint: a.balances
              .map((b) => formatAmount(b.balance, b.currency, i18n.language))
              .join(' · '),
          }))}
          selected={v[picker.list][picker.index]?.accountId ?? ''}
          onPick={(id) => {
            form.setValue(`${picker.list}.${picker.index}.accountId`, id);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {picker?.type === 'category' && (
        <CategoryPicker
          kind={kind}
          selected={picker.line === null ? v.categoryId : (v.lines[picker.line]?.categoryId ?? '')}
          onPick={(id) => {
            if (picker.line === null) form.setValue('categoryId', id);
            else form.setValue(`lines.${picker.line}.categoryId`, id);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </form>
  );
}

/** Under a payment line: the account it came from (or went to), and remove. */
function LineAccount({
  name,
  onPick,
  onRemove,
  removeLabel,
}: {
  name: string | undefined;
  onPick: () => void;
  onRemove?: () => void;
  removeLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2 border-t border-line pt-1">
      <span className="flex min-w-0 items-center gap-2">
        <span className="shrink-0 text-[13px] text-muted">{t('add.account')}</span>
        <Chip onClick={onPick}>{name ?? t('add.pickAccount')}</Chip>
      </span>
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className="flex size-11 shrink-0 items-center justify-center rounded-2xl text-muted hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
        >
          <Trash2 aria-hidden="true" className="size-5" strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

function SummaryRow({ label, value, tone }: { label: string; value: string; tone?: 'danger' }) {
  return (
    <li className="flex min-h-12 items-center justify-between gap-3 px-4 py-2">
      <span className="text-[13px] text-muted">{label}</span>
      <output
        dir="ltr"
        className={cn('text-[15px] font-semibold tabular-nums', tone === 'danger' && 'text-danger')}
      >
        {value}
      </output>
    </li>
  );
}
